import {
    ActionStatusHelper,
    DateFormatHelper,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    ActionResponse,
    useFormLifeCycle,
} from "@/Shared";

import { PlanningModel } from "../Models/PlanningModel";
import { PlanningFormViewModel } from "../ViewModels";
import { usePlanningEnricher } from "./usePlanningEnricher";
import { usePlanning } from "@/OneLiners";
import { PlanningValidationSchema } from "../PlanningFormValidation";

/**
 * usePlanningForm hook
 *
 * Domain-specific form hook responsible for creating and updating Planning entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - form view model construction,
 * - submission lifecycle (loading, success, error),
 * - UI feedback (toasts),
 * - and navigation or modal closing behavior.
 *
 * This hook supports two usage modes:
 * - Page mode (classic navigation flow)
 * - Modal mode (inline creation/editing from widgets or calendars)
 *
 * Author: Samano CASTRE
 * Date: July 2025
 */
export const usePlanningForm = (props: DomainHookProps): DomainFormHook => {
    const { extraArgs, permissionHook } = props;
    const { start, end } = extraArgs ?? {};
    const { domainType } = DOMAIN_CONFIGS.Planning;

    /**
     * Activity domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const planningHook = usePlanning();


    /**
     * Planning domain data and operations
     * Provided by the planning enricher facade
     */
    const {
        userMap,
        contactMap,
        propertyMap,
        enricherResponse,
    } = usePlanningEnricher();

    /**
         * Activity-related enums exposed to the UI layer.
         * Used to build the form view model.
         */
    const { PlanningEnums } = useAppEnum();

    /**
         * Aggregated response representing the global initialization state.
         * Combines:
         * - entity domain state
         * - permission loading state
         * - enrichment layer state
         */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            planningHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: planningHook.stateResponse.error ??
            enricherResponse.error
    };

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: planningHook,
        buildVM: (currentEntity: PlanningModel) => {

            if (start && end) {
                const startDate = DateFormatHelper.splitDateTime(start.toLocaleString('sv-SE'))
                const endDate = DateFormatHelper.splitDateTime(end.toLocaleString('sv-SE'))
                currentEntity ??= {
                    planningDate: startDate?.date,
                    startTime: startDate?.time,
                    endTime: endDate?.time,
                } as PlanningModel;
            }
            return new PlanningFormViewModel(
                PlanningEnums,
                contactMap,
                userMap,
                propertyMap,
                currentEntity
            )
        },
    });


    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        viewEnums: PlanningEnums,
        ValidationSchema: PlanningValidationSchema(PlanningEnums),
    }
}